/**
 * "Fresh AI texts lean on the best prompt groups" — the engine's own math,
 * shared so the editor draws exactly what the fill does (moved from the
 * backend's text-reuse, 15/10/2026). Each drop's fresh AI texts come from ONE
 * prompt group: without the lean the groups take turns; with it a group is
 * picked as often as its texts score (an unranked group gets the ranked
 * average so it can prove itself; every group keeps 5 % of the best one's
 * weight; no ranked score at all = no lean).
 * For Media Devoted | Created By Ciro Autiero
 */

/** No group falls under this share of the best one's weight. */
export const LEAN_FLOOR = 0.05;

/** Prompt group id → weight from the groups' ranked scores; null = no lean (turns). */
export function leanWeights(
  groupIds: string[],
  scoreOf: (groupId: string) => number | null | undefined,
): Map<string, number> | null {
  const ranked = groupIds
    .map(scoreOf)
    .filter((s): s is number => typeof s === "number")
    .map((s) => Math.max(s, 0));
  const best = Math.max(0, ...ranked);
  if (ranked.length === 0 || best <= 0) return null;
  const mean = ranked.reduce((a, b) => a + b, 0) / ranked.length;
  return new Map(
    groupIds.map((id) => {
      const s = scoreOf(id);
      const raw = typeof s === "number" ? Math.max(s, 0) : mean;
      return [id, Math.max(raw, best * LEAN_FLOOR)];
    }),
  );
}

/** group(index) for fresh AI drafts — a smooth weighted rotation (ROTATE) or a weighted draw (RANDOM). */
export function groupPicker(
  groups: string[],
  weights: Map<string, number> | null,
  mode: "ROTATE" | "RANDOM",
  rng: () => number,
): (index: number) => string {
  if (!weights)
    return mode === "RANDOM"
      ? () => groups[Math.floor(rng() * groups.length)]
      : (i) => groups[i % groups.length];
  const w = groups.map((g) => weights.get(g) ?? 0);
  const total = w.reduce((a, b) => a + b, 0);
  if (mode === "RANDOM")
    return () => {
      let r = rng() * total;
      for (let i = 0; i < groups.length; i += 1)
        if ((r -= w[i]) < 0) return groups[i];
      return groups[groups.length - 1];
    };
  return smoothRotation(groups, w, total);
}

function smoothRotation(groups: string[], w: number[], total: number): (index: number) => string {
  const current = groups.map(() => 0);
  const seq: string[] = [];
  return (index) => {
    while (seq.length <= index) {
      w.forEach((wi, i) => (current[i] += wi));
      const at = current.indexOf(Math.max(...current));
      current[at] -= total;
      seq.push(groups[at]);
    }
    return seq[index];
  };
}

/** How many of `drops` drafts each group writes — the rotation's own sequence. */
export function leanDropCounts(
  groups: string[],
  weights: Map<string, number> | null,
  drops: number,
): Map<string, number> {
  const pick = groupPicker(groups, weights, "ROTATE", () => 0);
  const counts = new Map(groups.map((g) => [g, 0]));
  for (let i = 0; i < drops && groups.length > 0; i += 1) {
    const g = pick(i);
    counts.set(g, (counts.get(g) ?? 0) + 1);
  }
  return counts;
}
